import React from "react";
import { Document, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Minimalist Pro Template - Clean, modern design with teal accent and elegant typography
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingTop: 40,
    paddingBottom: 30,
    paddingHorizontal: 40,
  },
  // Header with teal accent bar
  headerContainer: {
    backgroundColor: "#ffffff",
    marginHorizontal: -40,
    marginTop: -40,
    paddingHorizontal: 40,
    paddingTop: 30,
    paddingBottom: 20,
    borderTopWidth: 6,
    borderTopColor: "#0d9488",
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
    paddingRight: 20,
  },
  headerRight: {
    alignItems: "flex-end",
    paddingTop: 5,
  },
  // Name styling
  name: {
    fontSize: 26,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 4,
    letterSpacing: 0.5,
    lineHeight: 1.2,
  },
  title: {
    fontSize: 12,
    color: "#0d9488",
    fontWeight: 500,
    marginBottom: 0,
    letterSpacing: 0.3,
  },
  // Contact info - compact row
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  contactIcon: {
    width: 10,
    height: 10,
    marginRight: 6,
  },
  contactText: {
    fontSize: 9,
    color: "#6b7280",
  },
  // Main content area
  mainContent: {
    paddingTop: 10,
  },
  // Section styling
  sectionContainer: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  sectionAccent: {
    width: 4,
    height: 16,
    backgroundColor: "#0d9488",
    marginRight: 10,
    borderRadius: 2,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#111827",
    textTransform: "uppercase",
    letterSpacing: 1.5,
  },
  // Summary
  summaryText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: "#4b5563",
    textAlign: "justify",
  },
  // Experience items
  experienceItem: {
    marginBottom: 8,
    paddingBottom: 8,
  },
  experienceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 4,
  },
  experienceLeft: {
    flex: 1,
  },
  experienceRight: {
    alignItems: "flex-end",
    minWidth: 100,
  },
  jobTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 2,
  },
  companyName: {
    fontSize: 10,
    color: "#0d9488",
    fontWeight: 500,
  },
  dateText: {
    fontSize: 9,
    color: "#9ca3af",
    marginBottom: 2,
  },
  locationText: {
    fontSize: 9,
    color: "#9ca3af",
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 3,
    paddingLeft: 12,
    lineHeight: 1.5,
    color: "#4b5563",
  },
  // Education items
  educationItem: {
    marginBottom: 12,
  },
  educationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  degree: {
    fontSize: 11,
    fontWeight: 700,
    color: "#111827",
  },
  school: {
    fontSize: 10,
    color: "#6b7280",
    marginTop: 2,
  },
  educationDate: {
    fontSize: 9,
    color: "#9ca3af",
    textAlign: "right",
  },
  gpa: {
    fontSize: 9,
    color: "#9ca3af",
    textAlign: "right",
    marginTop: 2,
  },
  // Skills - pill style
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  skillPill: {
    backgroundColor: "#f0fdfa",
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#99f6e4",
    alignItems: "center",
    justifyContent: "center",
  },
  skillText: {
    fontSize: 9,
    color: "#0d9488",
    fontWeight: 500,
    textAlign: "center",
  },
  // Projects
  projectItem: {
    marginBottom: 10,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#111827",
  },
  projectTech: {
    fontSize: 9,
    color: "#6b7280",
    fontStyle: "italic",
    marginTop: 2,
  },
  // Certifications
  certificationItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    paddingBottom: 6,
  },
  certName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#111827",
  },
  certAuthority: {
    fontSize: 9,
    color: "#6b7280",
    marginTop: 1,
  },
  certDate: {
    fontSize: 9,
    color: "#9ca3af",
  },
  // Languages
  languageItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  languageName: {
    fontSize: 10,
    color: "#111827",
    fontWeight: 500,
    marginRight: 8,
  },
  languageLevel: {
    fontSize: 9,
    color: "#6b7280",
  },
  // Achievements
  achievementItem: {
    marginBottom: 6,
  },
  achievementText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#4b5563",
  },
});

// Contact icon paths
const contactIcons = {
  email: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  linkedin: "M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z",
};

const MinimalistProTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
    professionalTitle = "",
  } = resumeData || {};

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = { ...baseStyles.name, fontSize: fontSize * 1.85 };
      customStyleOverrides.title = { ...baseStyles.title, fontSize: fontSize * 0.85 };
      customStyleOverrides.sectionTitle = { ...baseStyles.sectionTitle, fontSize: fontSize * 0.8 };
      customStyleOverrides.jobTitle = { ...baseStyles.jobTitle, fontSize: fontSize * 0.8 };
      customStyleOverrides.degree = { ...baseStyles.degree, fontSize: fontSize * 0.8 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.72 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.72 };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionContainer = { ...baseStyles.sectionContainer, marginBottom: sectionSpacing };
    }

    if (titleColor) {
      // Header accent bar color
      customStyleOverrides.headerContainer = {
        ...baseStyles.headerContainer,
        borderTopColor: titleColor,
      };
      customStyleOverrides.name = {
        ...(customStyleOverrides.name || baseStyles.name),
        color: titleColor,
      };
      customStyleOverrides.sectionAccent = {
        ...baseStyles.sectionAccent,
        backgroundColor: titleColor,
      };
      customStyleOverrides.title = {
        ...(customStyleOverrides.title || baseStyles.title),
        color: titleColor,
      };
      customStyleOverrides.companyName = {
        ...baseStyles.companyName,
        color: titleColor,
      };
      // Skill pill styling with lighter background
      const lighterBg = titleColor + "15"; // Add transparency for lighter bg
      customStyleOverrides.skillPill = {
        ...baseStyles.skillPill,
        borderColor: titleColor,
        backgroundColor: lighterBg,
        alignItems: "center",
        justifyContent: "center",
      };
      customStyleOverrides.skillText = {
        ...baseStyles.skillText,
        color: titleColor,
        textAlign: "center",
      };
    }

    return StyleSheet.create({ ...baseStyles, ...customStyleOverrides });
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Default section titles
  const defaultTitles = {
    summary: 'Profile',
    workExperience: 'Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certifications',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards',
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry Expertise',
    publications: 'Publications',
    references: 'References',
  };

  // Get section title with fallback to default
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Default section order
  const defaultSectionOrder = [
    "summary",
    "workExperience",
    "education",
    "skills",
    "projects",
    "certifications",
    "languages",
    "achievements",
    "awards",
    "volunteering",
    "interests",
    "industryExpertise",
    "publications",
    "references",
  ];

  // Build final section order - include custom sections that aren't in the order
  const customSectionIds = customSections
    .filter(cs => cs.id && cs.title && cs.items?.length > 0)
    .map(cs => cs.id);

  const baseSectionOrder = customSectionOrder || defaultSectionOrder;

  // Add custom section IDs that aren't already in the order
  const finalSectionOrder = [
    ...baseSectionOrder,
    ...customSectionIds.filter(id => !baseSectionOrder.includes(id))
  ];

  // Combine all skills for display
  const allSkills = (() => {
    if (Array.isArray(skills)) return skills;
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) return skills.list;
    const combinedSkills = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];
    if (combinedSkills.length > 0) return combinedSkills;
    if (skills.categorized) {
      return [
        ...(skills.categorized.technical || []),
        ...(skills.categorized.soft || []),
        ...(skills.categorized.tools || []),
        ...(skills.categorized.other || []),
      ];
    }
    return [];
  })();

  const getSkillName = (skill) => {
    if (typeof skill === "string") return skill;
    if (skill && typeof skill === "object") return skill.name || skill.skill || "";
    return "";
  };

  // Helper to get language name from string or object
  const getLanguageName = (lang) => {
    if (typeof lang === "string") return lang;
    if (lang && typeof lang === "object") return lang.name || lang.language || "";
    return "";
  };

  // Helper to get interest name from string or object
  const getInterestName = (interest) => {
    if (typeof interest === "string") return interest;
    if (interest && typeof interest === "object") return interest.name || interest.title || "";
    return "";
  };

  // Render contact icon
  const renderContactIcon = (iconPath) => (
    <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
      <Path d={iconPath} fill="#9ca3af" />
    </Svg>
  );

  // Section components
  const sectionComponents = {
    summary: () =>
      summary &&
      isValidSummary(summary) && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>Profile</Text>
          </View>
          <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
        </View>
      ),

    workExperience: () =>
      workExperience.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>Experience</Text>
          </View>
          {workExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceHeader}>
                <View style={mergedStyles.experienceLeft}>
                  <Text style={mergedStyles.jobTitle}>{job.jobTitle}</Text>
                  <Text style={mergedStyles.companyName}>{job.company}</Text>
                </View>
                <View style={mergedStyles.experienceRight}>
                  <Text style={mergedStyles.dateText}>
                    {formatDate(job.startDate)} –{" "}
                    {job.isCurrentJob || job.endDate === "Present" || job.endDate === "Current" || !job.endDate
                      ? "Present"
                      : formatDate(job.endDate)}
                  </Text>
                  {job.location && <Text style={mergedStyles.locationText}>{job.location}</Text>}
                </View>
              </View>
              {job.description && (
                <View style={{ marginTop: 6 }}>
                  {renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    education: () =>
      education.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>Education</Text>
          </View>
          {education.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>
                    {edu.degree}
                    {edu.fieldOfStudy ? ` in ${edu.fieldOfStudy}` : ""}
                  </Text>
                  <Text style={mergedStyles.school}>
                    {edu.school}
                    {edu.location ? `, ${edu.location}` : ""}
                  </Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={mergedStyles.educationDate}>
                    {formatDate(edu.startDate)} – {edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                  </Text>
                  {edu.gpa && <Text style={mergedStyles.gpa}>GPA: {edu.gpa}</Text>}
                </View>
              </View>
            </View>
          ))}
        </View>
      ),

    skills: () =>
      allSkills.length > 0 && (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>Skills</Text>
          </View>
          <View style={mergedStyles.skillsContainer}>
            {allSkills.slice(0, 15).map((skill, index) => (
              <View key={index} style={mergedStyles.skillPill}>
                <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
              </View>
            ))}
          </View>
        </View>
      ),

    projects: () =>
      projects.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>Projects</Text>
          </View>
          {projects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {project.technologies && (
                <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>
              )}
              {project.description && (
                <View style={{ marginTop: 4 }}>
                  {renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    certifications: () =>
      certifications.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>Certifications</Text>
          </View>
          {certifications.map((cert, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <View style={{ flex: 1 }}>
                <Text style={mergedStyles.certName}>{cert.name}</Text>
                <Text style={mergedStyles.certAuthority}>{cert.authority || cert.issuer}</Text>
              </View>
              {cert.date && <Text style={mergedStyles.certDate}>{formatDate(cert.date)}</Text>}
            </View>
          ))}
        </View>
      ),

    languages: () =>
      languages &&
      languages.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>Languages</Text>
          </View>
          {languages.map((lang, index) => (
            <View key={index} style={mergedStyles.languageItem}>
              <Text style={mergedStyles.languageName}>{getLanguageName(lang)}</Text>
              {lang.proficiency && <Text style={mergedStyles.languageLevel}>– {lang.proficiency}</Text>}
            </View>
          ))}
        </View>
      ),

    achievements: () =>
      achievements.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('achievements')}</Text>
          </View>
          {achievements.map((achievement, index) => (
            <View key={index} style={mergedStyles.achievementItem}>
              {achievement.description && (
                <Text style={mergedStyles.achievementText}>{parseHtmlToText(achievement.description)}</Text>
              )}
            </View>
          ))}
        </View>
      ),

    awards: () =>
      awards.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('awards')}</Text>
          </View>
          {awards.map((award, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <View style={{ flex: 1 }}>
                <Text style={mergedStyles.certName}>{award.title || award.name}</Text>
                {award.issuer && <Text style={mergedStyles.certAuthority}>{award.issuer}</Text>}
              </View>
              {award.date && <Text style={mergedStyles.certDate}>{formatDate(award.date)}</Text>}
            </View>
          ))}
        </View>
      ),

    volunteering: () =>
      volunteering.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('volunteering')}</Text>
          </View>
          {volunteering.map((vol, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceHeader}>
                <View style={mergedStyles.experienceLeft}>
                  <Text style={mergedStyles.jobTitle}>{vol.role}</Text>
                  <Text style={mergedStyles.companyName}>{vol.organization}</Text>
                </View>
                <View style={mergedStyles.experienceRight}>
                  {(vol.startDate || vol.endDate) && (
                    <Text style={mergedStyles.dateText}>
                      {formatDate(vol.startDate)} – {vol.isCurrentRole ? "Present" : formatDate(vol.endDate)}
                    </Text>
                  )}
                </View>
              </View>
              {vol.description && (
                <View style={{ marginTop: 4 }}>
                  {renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    interests: () =>
      interests.length > 0 && (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
          </View>
          <View style={mergedStyles.skillsContainer}>
            {interests.map((interest, index) => (
              <View key={index} style={mergedStyles.skillPill}>
                <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
              </View>
            ))}
          </View>
        </View>
      ),

    industryExpertise: () =>
      industryExpertise.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
          </View>
          {industryExpertise.map((exp, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <View style={{ flex: 1 }}>
                <Text style={mergedStyles.certName}>{exp.name}</Text>
                {exp.yearsOfExperience && (
                  <Text style={mergedStyles.certAuthority}>{exp.yearsOfExperience} years experience</Text>
                )}
              </View>
              {exp.proficiency && <Text style={mergedStyles.certDate}>{exp.proficiency}</Text>}
            </View>
          ))}
        </View>
      ),

    publications: () =>
      publications.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('publications')}</Text>
          </View>
          {publications.map((pub, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <View style={{ flex: 1 }}>
                <Text style={mergedStyles.certName}>{pub.title}</Text>
                {pub.publisher && <Text style={mergedStyles.certAuthority}>{pub.publisher}</Text>}
              </View>
              {pub.date && <Text style={mergedStyles.certDate}>{formatDate(pub.date)}</Text>}
            </View>
          ))}
        </View>
      ),

    references: () =>
      references.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionHeader}>
            <View style={mergedStyles.sectionAccent} />
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('references')}</Text>
          </View>
          {references.map((ref, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <View style={{ flex: 1 }}>
                <Text style={mergedStyles.certName}>{ref.name}</Text>
                {ref.position && <Text style={mergedStyles.certAuthority}>{ref.position}</Text>}
                {ref.company && <Text style={mergedStyles.certAuthority}>{ref.company}</Text>}
                {ref.email && <Text style={mergedStyles.certAuthority}>{ref.email}</Text>}
              </View>
            </View>
          ))}
        </View>
      ),

    // Dynamic custom sections
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.sectionContainer}>
            <View style={mergedStyles.sectionHeader}>
              <View style={mergedStyles.sectionAccent} />
              <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
            </View>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={mergedStyles.achievementItem}>
                {item.content && (
                  <Text style={mergedStyles.achievementText}>{parseHtmlToText(item.content)}</Text>
                )}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {}),
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header Section with Teal Accent */}
        <View style={mergedStyles.headerContainer}>
          <View style={mergedStyles.headerContent}>
            <View style={mergedStyles.headerLeft}>
              {/* Name */}
              <Text style={mergedStyles.name}>
                {personalInfo.firstName} {personalInfo.lastName}
              </Text>
              {/* Professional Title */}
              {(professionalTitle || (workExperience.length > 0 && workExperience[0].jobTitle)) && (
                <Text style={mergedStyles.title}>{professionalTitle || workExperience[0].jobTitle}</Text>
              )}
            </View>

            <View style={mergedStyles.headerRight}>
              {/* Email */}
              {personalInfo.email && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.email)}
                  <Text style={mergedStyles.contactText}>{personalInfo.email}</Text>
                </View>
              )}
              {/* Phone */}
              {personalInfo.phone && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.phone)}
                  <Text style={mergedStyles.contactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
                </View>
              )}
              {/* Location */}
              {personalInfo.location && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.location)}
                  <Text style={mergedStyles.contactText}>{personalInfo.location}</Text>
                </View>
              )}
              {/* LinkedIn */}
              {personalInfo.linkedin && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.linkedin)}
                  <Text style={mergedStyles.contactText}>{personalInfo.linkedin}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Main Content */}
        <View style={mergedStyles.mainContent}>
          {finalSectionOrder.map((sectionId, index) => {
            const SectionComponent = sectionComponents[sectionId];
            const content = SectionComponent ? SectionComponent() : null;
            return content ? <View key={`${sectionId}-${index}`} wrap={true}>{content}</View> : null;
          })}
        </View>
      </Page>
    </Document>
  );
};

export default MinimalistProTemplate;
